import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { MagnifyingGlassIcon, MapPinIcon, PlusIcon, TrashIcon, XIcon } from '@phosphor-icons/react';
import type { ConstellationPlace, PlaceKind } from '../../../services/dashboard.service';
import type { UseTerritoriesApi } from '../hooks/useTerritoriesApi';

const KIND_LABEL: Record<PlaceKind, string> = { city: 'City', state: 'State', country: 'Country' };
const MAX_SUGGESTIONS = 8;

interface LocationPickerProps {
  places: ConstellationPlace[];
  placesLoading: boolean;
  placeId: string;
  selectedName?: string;
  radiusKm: number;
  minRadius: number;
  maxRadius: number;
  territoryId: string;
  onPlaceChange: (placeId: string) => void;
  onRadiusChange: (radiusKm: number) => void;
  onTerritoryChange: (territoryId: string) => void;
  territoriesApi: UseTerritoriesApi;
}

export const LocationPicker = ({
  places,
  placesLoading,
  placeId,
  selectedName,
  radiusKm,
  minRadius,
  maxRadius,
  territoryId,
  onPlaceChange,
  onRadiusChange,
  onTerritoryChange,
  territoriesApi,
}: LocationPickerProps) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Admin Territory creation state
  const [isCreatingTerritory, setIsCreatingTerritory] = useState(false);
  const [newTerritoryLabel, setNewTerritoryLabel] = useState('');
  const [newTerritoryOwner, setNewTerritoryOwner] = useState('');

  // Admin Place adding state
  const [isAddingPlace, setIsAddingPlace] = useState(false);

  const byId = useMemo(() => new Map(places.map((p) => [p.id, p])), [places]);
  const selected = byId.get(placeId);
  const selectedKind = selected?.kind ?? (placeId.split(':')[0] as PlaceKind);
  
  const territory = territoriesApi.territories.find((t) => t.id === territoryId);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return places.slice(0, MAX_SUGGESTIONS);
    const starts: ConstellationPlace[] = [];
    const contains: ConstellationPlace[] = [];
    for (const p of places) {
      const name = p.name.toLowerCase();
      if (name.startsWith(q)) starts.push(p);
      else if (name.includes(q) || p.region.toLowerCase().includes(q)) contains.push(p);
      if (starts.length >= MAX_SUGGESTIONS) break;
    }
    return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
  }, [places, query]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const choose = async (id: string) => {
    if (isAddingPlace && territory) {
      const placeObj = byId.get(id);
      if (placeObj) {
        try {
          await territoriesApi.addPlace(territory.id, { id: placeObj.id, label: placeObj.name });
        } catch (err: any) {
          console.error('Failed to add place', err);
          alert(`Failed to add place: ${err.message || 'Unknown error'}`);
        }
      }
      setIsAddingPlace(false);
      setQuery('');
      setOpen(false);
      return;
    }

    onPlaceChange(id);
    onTerritoryChange(''); // Clear territory selection when picking a specific place
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open && suggestions[active]) {
      e.preventDefault();
      choose(suggestions[active].id);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setIsAddingPlace(false);
    }
  };

  const handleCreateTerritory = async () => {
    if (!newTerritoryLabel.trim()) {
      alert('Please provide a Label for the territory.');
      return;
    }
    const owner = newTerritoryOwner.trim() || 'Admin';
    try {
      const created = await territoriesApi.create(newTerritoryLabel.trim(), owner);
      onTerritoryChange(created.id);
      setIsCreatingTerritory(false);
      setIsAddingPlace(true); // Automatically open the add place search
      setNewTerritoryLabel('');
      setNewTerritoryOwner('');
    } catch (err: any) {
      console.error('Failed to create territory', err);
      alert(`Failed to create territory: ${err.message || 'Unknown error'}`);
    }
  };

  const handleDeleteTerritory = () => {
    if (territory && confirm(`Are you sure you want to delete ${territory.label}?`)) {
      territoriesApi.remove(territory.id);
      onTerritoryChange('');
    }
  };

  const labelClass = 'text-[10px] text-slate-400 font-bold uppercase tracking-wider';
  const controlClass =
    'bg-slate-50 border border-slate-200/60 focus:border-accent focus:ring-4 focus:ring-accent/10 focus:bg-white outline-none rounded-lg px-2.5 py-1.5 text-xs text-slate-800 font-medium transition-smooth';

  return (
    <div className="px-5 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-50 bg-white relative z-20">
      <div className="flex items-center gap-1.5">
        <span className={labelClass}>Territory</span>
        
        {!isCreatingTerritory ? (
          <select 
            value={territoryId} 
            onChange={(e) => {
              onTerritoryChange(e.target.value);
              if (e.target.value) onPlaceChange(''); // Clear place when territory selected
            }} 
            className={controlClass}
            disabled={territoriesApi.loading}
          >
            <option value="">Any place</option>
            {territoriesApi.territories.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label} · {t.owner}
              </option>
            ))}
          </select>
        ) : (
          <div className="flex items-center gap-1">
            <input 
              type="text" 
              placeholder="Label" 
              value={newTerritoryLabel}
              onChange={e => setNewTerritoryLabel(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateTerritory()}
              className={controlClass} 
              autoFocus
            />
            <input 
              type="text" 
              placeholder="Owner" 
              value={newTerritoryOwner}
              onChange={e => setNewTerritoryOwner(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateTerritory()}
              className={controlClass} 
            />
            <button 
              onClick={handleCreateTerritory}
              className="px-2 py-1.5 bg-accent text-white text-xs rounded-lg font-medium hover:bg-accent/90"
            >
              Save
            </button>
            <button 
              onClick={() => setIsCreatingTerritory(false)}
              className="px-2 py-1.5 text-slate-500 hover:text-slate-700"
            >
              <XIcon size={14} weight="bold" />
            </button>
          </div>
        )}

        {territoriesApi.isAdmin && !isCreatingTerritory && (
          <button 
            onClick={() => setIsCreatingTerritory(true)}
            className="p-1.5 text-slate-400 hover:text-accent hover:bg-accent/10 rounded transition-colors"
            title="Create new territory"
          >
            <PlusIcon size={14} weight="bold" />
          </button>
        )}

        {territoriesApi.isAdmin && territory && !isCreatingTerritory && (
          <button 
            onClick={handleDeleteTerritory}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
            title="Delete territory"
          >
            <TrashIcon size={14} weight="bold" />
          </button>
        )}
      </div>

      {territory && (
        <div className="flex flex-wrap items-center gap-1.5">
          {territory.places.map(({ id, label }) => {
            const place = byId.get(id);
            const isSelected = id === placeId;
            const unavailable = !placesLoading && !place;
            return (
              <div key={id} className="relative group flex items-center">
                <button
                  onClick={() => choose(id)}
                  disabled={unavailable}
                  aria-pressed={isSelected}
                  title={
                    unavailable
                      ? 'No alumni located here yet'
                      : place
                        ? `${place.name} · ${KIND_LABEL[place.kind]} · ${place.count.toLocaleString('en-IN')} alumni`
                        : label
                  }
                  className={`px-2 py-1 rounded-lg border text-[10px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    isSelected
                      ? 'bg-accent/10 border-accent/30 text-accent'
                      : 'bg-slate-50 border-slate-200/60 text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                  }`}
                >
                  {label}
                  {place && (
                    <span className="ml-1 font-medium text-slate-400 tabular-nums">
                      {place.count.toLocaleString('en-IN')}
                    </span>
                  )}
                </button>
                {territoriesApi.isAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      territoriesApi.removePlace(territory.id, id);
                    }}
                    className="absolute -top-1.5 -right-1.5 bg-red-100 text-red-600 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                  >
                    <XIcon size={8} weight="bold" />
                  </button>
                )}
              </div>
            );
          })}
          
          {territoriesApi.isAdmin && (
            <button
              onClick={() => setIsAddingPlace(!isAddingPlace)}
              className={`px-2 py-1 rounded-lg border border-dashed text-[10px] font-semibold flex items-center gap-1 transition-colors ${
                isAddingPlace 
                  ? 'bg-accent/10 border-accent text-accent' 
                  : 'bg-transparent border-slate-300 text-slate-500 hover:bg-slate-50'
              }`}
            >
              <PlusIcon size={10} weight="bold" /> Add
            </button>
          )}
        </div>
      )}

      {(!territory || isAddingPlace) && (
        <div ref={boxRef} className="relative">
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200/60 rounded-lg pl-2.5 pr-1 focus-within:border-accent focus-within:ring-4 focus-within:ring-accent/10 focus-within:bg-white transition-smooth">
            <MagnifyingGlassIcon size={12} weight="bold" className="text-slate-400 shrink-0" />
            <input
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-controls="location-picker-options"
              aria-autocomplete="list"
              value={query}
              placeholder={placesLoading ? 'Loading places…' : (isAddingPlace ? 'Search to add to territory' : 'Search city, state or country')}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              className="w-52 bg-transparent outline-none py-1.5 text-xs text-slate-800 placeholder-slate-400 font-medium"
            />
          </div>
          {open && suggestions.length > 0 && (
            <ul
              id="location-picker-options"
              role="listbox"
              className="absolute left-0 mt-1 w-80 max-h-72 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg py-1"
            >
              {suggestions.map((p, i) => (
                <li
                  key={p.id}
                  role="option"
                  aria-selected={i === active}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    choose(p.id);
                  }}
                  onMouseEnter={() => setActive(i)}
                  className={`px-3 py-1.5 flex items-center gap-2 cursor-pointer ${i === active ? 'bg-slate-50' : ''}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-slate-800 truncate">{p.name}</div>
                    {p.region && <div className="text-[10px] text-slate-400 truncate">{p.region}</div>}
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{KIND_LABEL[p.kind]}</span>
                  <span className="text-[10px] tabular-nums text-slate-500 w-12 text-right">{p.count.toLocaleString('en-IN')}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!territory && (
        <div className="flex items-center gap-1 min-w-0 text-[11px] font-semibold text-slate-700">
          <MapPinIcon size={13} weight="fill" className="text-accent shrink-0" />
          <span className="truncate">{selected?.name ?? selectedName ?? placeId}</span>
          {selected?.region && <span className="text-slate-400 font-medium truncate">· {selected.region}</span>}
        </div>
      )}

      {selectedKind === 'city' && !territoryId && (
        <div className="flex items-center gap-2 ml-auto">
          <span className={labelClass}>Radius</span>
          <div className="flex items-center gap-2 bg-slate-50 border border-slate-200/60 rounded-lg px-3 py-1.5 min-w-[140px]">
            <input
              type="range"
              min={minRadius}
              max={maxRadius}
              step={10}
              value={radiusKm}
              onChange={(e) => onRadiusChange(parseInt(e.target.value, 10))}
              className="w-24 accent-accent cursor-pointer"
            />
            <span className="text-[10px] font-semibold tabular-nums text-slate-700 w-12 text-right">
              {radiusKm.toLocaleString('en-IN')} km
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
